'use client';

/**
 * Application Providers
 *
 * This file sets up all required providers for the application:
 * - QueryClientProvider: TanStack Query for server state management
 * - AuthProvider: Initializes and manages authentication state
 * - Toaster: Toast notifications for user feedback
 *
 * The providers are composed in a specific order to ensure proper
 * dependency resolution and context availability.
 */

import { useEffect, type ReactNode } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from '@/components/ui/toaster';
import { useAuthStore } from '@/stores/authStore';

/**
 * QueryClient configuration with sensible defaults for RWA Console
 *
 * - staleTime: 5 minutes - data is considered fresh for 5 minutes
 * - gcTime: 10 minutes - unused data is garbage collected after 10 minutes
 * - refetchOnWindowFocus: true - refetch when user returns to tab
 * - retry: 3 - retry failed requests up to 3 times with exponential backoff
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 10, // 10 minutes
      refetchOnWindowFocus: true,
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    },
    mutations: {
      retry: 1,
      retryDelay: 1000,
    },
  },
});

/**
 * AuthProvider component
 *
 * Guards the app while auth state is being initialized.
 *
 * Session restoration strategy (3 layers):
 * 1. Zustand persist hydrates both token+user from localStorage (sync)
 * 2. onRehydrateStorage calls initialize() after hydration
 * 3. useEffect calls initialize() as a safety net (handles edge cases
 *    where onRehydrateStorage might not fire or fires too early)
 *
 * initialize() itself reads localStorage directly as a fallback,
 * so it works correctly even if called before persist hydration.
 */
function AuthProvider({ children }: { children: ReactNode }) {
  const initialize = useAuthStore((state) => state.initialize);
  const isLoading = useAuthStore((state) => state.isLoading);

  useEffect(() => {
    initialize();
  }, [initialize]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 dark:bg-slate-900">
        <div className="flex flex-col items-center gap-4">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600 dark:border-primary-800 dark:border-t-primary-400" />
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Initializing...
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

/**
 * Application Providers Props
 */
interface AppProvidersProps {
  /** Child components to wrap with providers */
  children: ReactNode;
}

/**
 * AppProviders - Root provider composition
 *
 * Wraps the application with all necessary context providers in the correct order:
 * 1. QueryClientProvider - Must be at the top for query hooks to work
 * 2. AuthProvider - Initializes auth state and handles session
 * 3. Toaster - Renders toast notifications
 *
 * Usage in main.tsx or App.tsx:
 * ```tsx
 * <AppProviders>
 *   <Router>
 *     <App />
 *   </Router>
 * </AppProviders>
 * ```
 */
export function AppProviders({ children }: AppProvidersProps) {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        {children}
        <Toaster />
      </AuthProvider>
    </QueryClientProvider>
  );
}

/**
 * Export QueryClient instance for use in prefetching
 * or other advanced use cases outside of React components
 */
export { queryClient };
